import React from 'react';
import Icon from 'elements/Icon';
import { shouldShowToolCall, getToolCallMessage } from './toolCallConfig';

interface ToolCallPart {
    type: 'tool_call';
    toolName: string;
    message: string;
    status: 'running' | 'success' | 'error';
    startTime: number;
    endTime?: number;
    id: string;
    groupId?: string;
}

interface ToolCallIndicatorProps {
    toolCall: ToolCallPart;
}


const ToolCallIndicator: React.FC<ToolCallIndicatorProps> = ({ toolCall }) => {
    const { toolName, message, status } = toolCall;

    // Check if this tool call should be shown
    if (!shouldShowToolCall(toolName)) {
        // Return spacing to prevent text from being smushed together
        return <div style={{ margin: '12px 0' }} />;
    }

    // Get the custom message for this tool call
    const displayMessage = getToolCallMessage(toolName, status, message);

    const keyframes = `
        @keyframes shimmer-animation {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `;

    // A more cohesive and sophisticated style theme
    const styles: any = {
        running: {
            bg: '#4c1d95',      // Deep purple background for the active state
            border: '#6d28d9',
            text: '#e0e7ff',      // Light lavender text
        },
        success: {
            bg: '#f5f3ff',      // Light lavender background for success
            border: '#ddd6fe',
            text: '#5b21b6',      // Deep purple text
            icon: "check",
            iconColor: '#5b21b6'
        },
        error: {
            bg: '#fef2f2',      // Light rose background for error
            border: '#fecaca',
            text: '#b91c1c',      // Strong red text
            icon: "closeCircle",
            iconColor: '#b91c1c'
        }
    };

    const currentStyle = styles[status];

    const runningStyles = status === 'running' ? {
        backgroundImage: `linear-gradient(to right, ${currentStyle.bg} 0%, #6d28d9 50%, ${currentStyle.bg} 100%)`,
        backgroundSize: '800px 100%',
        animation: 'shimmer-animation 2s infinite linear',
    } : {};

    return (
        <>
            <style>{keyframes}</style>
            <div
                className="tool-call-indicator"
                style={{
                    display: 'inline-flex', // CHANGE: Makes the container fit its content
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: currentStyle.bg,
                    border: `1px solid ${currentStyle.border}`,
                    margin: '12px 0',
                    marginTop: 0,
                    marginRight: '8px',
                    transition: 'all 0.3s ease',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    overflow: 'hidden',
                    maxWidth: '100%', // Prevents overflow on small screens
                    ...runningStyles
                }}
            >
                {currentStyle.icon && currentStyle.iconColor ? <Icon className="toolCallRunningIcon" icon={currentStyle.icon} size={16} color={currentStyle.iconColor} /> : null}
                <span style={{
                    fontSize: '14px',
                    color: currentStyle.text,
                    fontWeight: 500
                }}>
                    {displayMessage}
                </span>

                {/* REMOVED: The timing span is no longer here */}
            </div>
        </>
    );
};
// Optional: Component for grouping parallel tool calls
interface ToolCallGroupProps {
    toolCalls: ToolCallPart[];
}

export const ToolCallGroup: React.FC<ToolCallGroupProps> = ({ toolCalls }) => {
    // Filter out tool calls that shouldn't be shown
    const visibleToolCalls = toolCalls.filter(tc => shouldShowToolCall(tc.toolName));

    // If no tool calls should be shown, don't render the group
    if (visibleToolCalls.length === 0) {
        return null;
    }

    return (
        <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '12px',
            margin: '12px 0',
            backgroundColor: '#f9fafb'
        }}>
            <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px',
                fontWeight: 500
            }}>
                Running {visibleToolCalls.length} tools in parallel
            </div>
            {visibleToolCalls.map(toolCall => (
                <ToolCallIndicator key={toolCall.id} toolCall={toolCall} />
            ))}
        </div>
    );
};

export default ToolCallIndicator;